/**
 * `Project_Executor.ts` — port of Unity's `Executor/Project_Executor.cs`.
 *
 * Navigation goes through the app router (web equivalent of Unity's
 * `IAsyncNavigationModel`): opening a project routes to the Editor view,
 * which drives `ProjectModel.loadProjectAsync` on mount.
 */

import { router } from "@router/index";
import type { IProjectListModel } from "@models/project/IProjectListModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  getString,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class ProjectExecutor implements IDomainExecutor {
  private readonly m_projectList: IProjectListModel;
  private readonly m_project: IProjectModel;

  constructor(projectList: IProjectListModel, project: IProjectModel) {
    this.m_projectList = projectList;
    this.m_project = project;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.GetAvailableProjects:
        return this.getAvailableProjects();
      case AgentToolNames.OpenProject:
        return this.openProject(args);
      case AgentToolNames.ExitProject:
        return this.exitProject();
      default:
        return null;
    }
  }

  private async getAvailableProjects(): Promise<string> {
    const projects = await this.m_projectList.refreshAsync();
    if (projects.length === 0) return "ok: no projects available";
    const lines = projects.map(
      (p) => `"${p.name}" (project_id:${p.id})${p.category ? ` [${p.category}]` : ""}`,
    );
    return lines.join("\n");
  }

  private async openProject(args: ToolArgs): Promise<string> {
    const projectId = getString(args, "project_id");
    if (!projectId) return "error: Open_Project_missing_project_id";
    const project =
      this.m_projectList.getById(projectId) ??
      (await this.m_projectList.refreshAsync()).find((p) => p.id === projectId) ??
      null;
    if (!project) {
      return `error: project '${projectId}' not found. Use Get_Available_Projects for valid project_id values.`;
    }
    await router.push({ name: "Editor", params: { projectId } });
    return `ok: opening project '${project.name}'`;
  }

  private async exitProject(): Promise<string> {
    if (!this.m_project.activeProject) return "ok: no project is open";
    await router.push({ name: "Projects" });
    return "ok: exited to the projects list";
  }
}
